import { useState, useEffect } from 'react';
import api from '../utils/api';
import { useToast } from '../components/ToastContext';

/**
 * Hook to monitor auto-sync status and show notifications when sync completes
 * @param {boolean} enabled - Whether to check for auto-sync (true/false)
 * @returns {Object} Sync status information
 */
export function useAutoSync(enabled = true) {
  const { showToast } = useToast();
  const [lastSyncTime, setLastSyncTime] = useState(null);
  const [syncStatus, setSyncStatus] = useState(null);

  useEffect(() => {
    if (!enabled) return;

    let checkInterval = null;

    const checkAutoSync = async () => {
      try {
        const response = await api.get('/auto-sync/status?next_check=60');
        const data = response.data;

        // Check if there's a new successful sync that we haven't seen yet
        const currentLastSyncTime = new Date(data.last_sync_time || 0);
        const previousLastSyncTime = lastSyncTime ? new Date(lastSyncTime) : null;

        if (data.enabled && data.last_sync_status === 'success' && 
            (previousLastSyncTime === null || 
             currentLastSyncTime > previousLastSyncTime)) {
          
          // Show notification about auto-sync completion
          const hours = currentLastSyncTime.getHours().toString().padStart(2, '0');
          const minutes = currentLastSyncTime.getMinutes().toString().padStart(2, '0');
          const seconds = currentLastSyncTime.getSeconds().toString().padStart(2, '0');
          
          showToast(
            `✅ Auto-sync completed successfully! Data synced at ${hours}:${minutes}:${seconds}`,
            'success',
            8000
          );

          setLastSyncTime(data.last_sync_time);
          setSyncStatus('success');
        } else if (data.last_sync_status === 'failed') {
          // Show error notification
          showToast(
            `❌ Auto-sync failed: ${data.last_error || 'Unknown error'}`,
            'error',
            15000
          );

          setSyncStatus('failed');
        }
      } catch (error) {
        console.error('Failed to check auto-sync status:', error);
      }
    };

    // Initial check
    checkAutoSync();

    // Set up periodic checking (every 30 seconds by default, or as specified in query)
    const intervalMs = parseInt(localStorage.getItem('auto_sync_check_interval') || '30000', 10);
    checkInterval = setInterval(checkAutoSync, intervalMs);

    return () => {
      if (checkInterval) {
        clearInterval(checkInterval);
      }
    };
  }, [enabled, lastSyncTime, showToast]);

  return {
    lastSyncTime,
    syncStatus,
    isSyncing: syncStatus !== null
  };
}
